import React, { useState, useEffect } from 'react';
import {
  Box, CssBaseline, AppBar, Toolbar, Typography, Grid, Drawer, Card, 
  CardContent, Avatar, IconButton, Badge, Button, Divider, List, Paper, Snackbar,
  Alert, Dialog, DialogContent, DialogTitle, useMediaQuery, CircularProgress,
  Tooltip, TextField, Chip
} from '@mui/material';
import { ThemeProvider, createTheme, alpha } from '@mui/material/styles';
import { signOut } from 'firebase/auth';
import { collection, query, orderBy, onSnapshot, doc, getDoc, addDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { format, parseISO, isValid } from 'date-fns';
import { auth, db } from './firebase';
import { heIL } from '@mui/material/locale';
import { safeFormatDate } from './TradeForm';
import { getTradeR } from './StatsUtils';

// Import Components
import TradeForm from './TradeForm';
import TradeTable from './TradeTable';
// Import Icons
import ShowChartIcon from '@mui/icons-material/ShowChart';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import FeedbackIcon from '@mui/icons-material/Feedback';
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import LabelIcon from '@mui/icons-material/Label';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import RefreshIcon from '@mui/icons-material/Refresh';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CelebrationIcon from '@mui/icons-material/Celebration';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';

// Create RTL dark theme
const darkTheme = createTheme({
  direction: 'rtl',
  palette: {
    mode: 'dark',
    primary: {
      main: '#3a86ff',
      light: '#60a5fa',
      dark: '#2563eb',
    },
    secondary: {
      main: '#8b5cf6',
      light: '#a78bfa',
      dark: '#7c3aed',
    },
    success: {
      main: '#10b981',
      light: '#34d399',
      dark: '#059669',
    },
    error: {
      main: '#ef4444',
      light: '#f87171',
      dark: '#dc2626',
    },
    warning: {
      main: '#f59e0b',
      light: '#fbbf24',
      dark: '#d97706',
    },
    info: {
      main: '#3b82f6',
      light: '#60a5fa',
      dark: '#2563eb',
    },
    background: {
      default: '#111827',
      paper: '#1f2937',
    },
    text: {
      primary: '#f3f4f6',
      secondary: '#9ca3af',
    },
    divider: 'rgba(107, 114, 128, 0.3)',
  },
  typography: {
    fontFamily: '"Rubik", "Roboto", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
    },
    h2: {
      fontWeight: 700,
    },
    h3: {
      fontWeight: 700,
    },
    h4: {
      fontWeight: 700,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
    button: {
      fontWeight: 500,
      textTransform: 'none',
    },
  },
  shape: {
    borderRadius: 16,
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
          backgroundImage: 'none',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          textTransform: 'none',
          boxShadow: 'none',
          fontSize: '0.875rem',
          fontWeight: 500,
          height: 42,
          '&:hover': {
            boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          overflow: 'hidden',
          transition: 'transform 0.3s, box-shadow 0.3s',
          '&:hover': {
            boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid rgba(107, 114, 128, 0.15)',
        },
        head: {
          fontWeight: 600,
        },
      },
    },
  },
}, heIL);

const Dashboard = () => {
  const navigate = useNavigate();
  const user = auth.currentUser;
  const drawerWidth = 300;
  const isMobile = useMediaQuery(darkTheme.breakpoints.down('md'));

  // States
  const [currentDate, setCurrentDate] = useState(new Date());
  const [complaints, setComplaints] = useState([]);
  const [complaintText, setComplaintText] = useState("");
  const [complaintDialogOpen, setComplaintDialogOpen] = useState(false);
  const [complaintsDrawerOpen, setComplaintsDrawerOpen] = useState(false);
  const [errorSnackbarOpen, setErrorSnackbarOpen] = useState(false);
  const [errorSnackbarMessage, setErrorSnackbarMessage] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [userProfile, setUserProfile] = useState({
    trigger: '',
    tradingHours: '',
  });
  const [trades, setTrades] = useState([]);
  const [showTradeForm, setShowTradeForm] = useState(false);
  const [notes, setNotes] = useState([]);
  
  // Daily goal celebration
  const [showDailyGoalCelebration, setShowDailyGoalCelebration] = useState(false);
  const [hasShownDailyGoalToday, setHasShownDailyGoalToday] = useState(false);

  // Error Snackbar Handling
  const showErrorMessage = (msg) => {
    setErrorSnackbarMessage(msg);
    setErrorSnackbarOpen(true);
  };

  const handleCloseErrorSnackbar = () => {
    setErrorSnackbarOpen(false);
  };

  // Complaints Handling
  const handleToggleComplaintsDrawer = () => {
    setComplaintsDrawerOpen(!complaintsDrawerOpen);
  };

  const handleSaveComplaint = async () => {
    if (!complaintText.trim()) {
      return;
    }
    try {
      await addDoc(collection(db, 'complaints'), {
        complaint: complaintText,
        uid: user.uid,
        userName: user.displayName || user.email,
        createdAt: new Date()
      });
      setComplaintText("");
      setComplaintDialogOpen(false);
      
      // Refresh complaints
      fetchComplaints();
    } catch (error) {
      console.error("שגיאה בשמירת תלונה:", error);
    }
  };

  const fetchComplaints = () => {
    try {
      const complaintsRef = collection(db, 'complaints');
      const q = query(complaintsRef, orderBy('createdAt', 'desc'));
      
      const unsubscribe = onSnapshot(q, (snapshot) => {
        const complaintsData = [];
        snapshot.forEach((doc) => {
          const data = doc.data();
          // Convert Timestamp to full date
          const createdAt = data.createdAt instanceof Date ? 
            data.createdAt : 
            (data.createdAt ? new Date(data.createdAt) : new Date());
          
          complaintsData.push({
            id: doc.id,
            ...data,
            createdAt,
            formattedDate: safeFormatDate(createdAt, 'dd/MM/yyyy HH:mm')
          });
        });
        setComplaints(complaintsData);
      });
      
      return unsubscribe;
    } catch (error) {
      console.error("שגיאה בטעינת תלונות:", error);
    }
  };

  // Check if daily goal reached
  const checkDailyGoal = (tradesData) => {
    const todayStr = safeFormatDate(currentDate, 'yyyy-MM-dd');
    const tradesTodayCount = tradesData.filter((t) => t.date === todayStr).length;
    
    // Check if reached 40 trades today and haven't shown celebration yet
    if (tradesTodayCount >= 50 && !hasShownDailyGoalToday) {
      const lastShownDate = localStorage.getItem('lastDailyGoalShown');
      if (lastShownDate !== todayStr) {
        setShowDailyGoalCelebration(true);
        setHasShownDailyGoalToday(true);
        localStorage.setItem('lastDailyGoalShown', todayStr);
      }
    }
  };

  // Data Fetching
  useEffect(() => {
    if (!user) return;
    // Update current date
    const timer = setInterval(() => {
      setCurrentDate(new Date());
    }, 60000);

    // Fetch trades
    const tradesRef = collection(db, 'trades');
    const q = query(tradesRef, orderBy('date', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = [];
      snapshot.forEach((docSnap) => {
        const d = docSnap.data();
        if (d.uid === user.uid) {
          data.push({ id: docSnap.id, ...d });
        }
      });
      setTrades(data);
      checkDailyGoal(data); // Check daily goal when trades update
    });

    // Fetch user profile
    const fetchUserProfile = async () => {
      try {
        const docRef = doc(db, 'users', user.uid);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setUserProfile(docSnap.data());
        }
      } catch (error) {
        console.error('שגיאה בטעינת פרופיל משתמש:', error);
      }
    };
    fetchUserProfile();

    // Fetch complaints and notes
    const unsubscribeComplaints = fetchComplaints();
    
    // Fetch notes
    const notesRef = collection(db, 'notes');
    const unsubscribeNotes = onSnapshot(notesRef, (snapshot) => {
      const notesData = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setNotes(notesData);
    });

    return () => {
      clearInterval(timer);
      unsubscribe();
      if (unsubscribeComplaints) unsubscribeComplaints();
      unsubscribeNotes();
    };
  }, [user, currentDate, hasShownDailyGoalToday]);

  // Logout handler
  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      console.error('שגיאה בהתנתקות:', error);
    }
  };

  // Import/Export Handlers
  const handleImportCSV = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setIsSaving(true);
    
    // This would need to be implemented based on your CSV format
    // For now, we'll just show a snackbar
    setTimeout(() => {
      setSnackbarMessage("CSV יובא בהצלחה");
      setSnackbarOpen(true);
      setIsSaving(false);
    }, 1500);
  };

  const handleExportCSV = () => {
    setIsSaving(true);
    
    // This would need to be implemented based on your data format
    // For now, we'll just show a snackbar
    setTimeout(() => {
      setSnackbarMessage("CSV יוצא בהצלחה");
      setSnackbarOpen(true);
      setIsSaving(false);
    }, 1500);
  };

  // Statistics calculation - Fixed R calculator
  const realTrades = trades.filter((t) => t.triggerAppeared === 'yes');
  const totalR = realTrades.reduce((acc, trade) => acc + getTradeR(trade), 0);
  const totalRealTrades = realTrades.length;
  const avgR = totalRealTrades > 0 ? totalR / totalRealTrades : 0;
  const winCount = realTrades.filter((t) => t.outcome === 'Take Profit').length;
  const winRate = totalRealTrades > 0 ? (winCount / totalRealTrades) * 100 : 0;
  
  const executedTradesCount = totalRealTrades;
  const todayStr = safeFormatDate(currentDate, 'yyyy-MM-dd');
  const tradesTodayCount = trades.filter((t) => t.date === todayStr).length;
  const tradesLeftToday = Math.max(0, 50 - tradesTodayCount);
  const tradesLeft = Math.max(0, 400 - realTrades.length);
  const currentTrigger = userProfile.trigger || 'לא מוגדר';
  const tradingHours = userProfile.tradingHours || 'לא מוגדר';

  // Calculate total days recorded and current week days
  const uniqueDaysRecorded = [...new Set(trades.map(t => t.date))].length;
  
  // Calculate current week days (Sunday to Saturday)
  const currentWeekStart = new Date(currentDate);
  currentWeekStart.setDate(currentDate.getDate() - currentDate.getDay()); // Start of week (Sunday)
  currentWeekStart.setHours(0, 0, 0, 0);
  
  const currentWeekEnd = new Date(currentWeekStart);
  currentWeekEnd.setDate(currentWeekStart.getDate() + 6); // End of week (Saturday)
  currentWeekEnd.setHours(23, 59, 59, 999);
  
  const currentWeekDaysRecorded = [...new Set(
    trades
      .filter(t => {
        const tradeDate = new Date(t.date);
        return tradeDate >= currentWeekStart && tradeDate <= currentWeekEnd;
      })
      .map(t => t.date)
  )].length;

  // iframe calendar URL
  const iFrameUrl =
    'https://sslecal2.investing.com?columns=exc_flags,exc_currency,exc_importance,exc_actual,exc_forecast,exc_previous' +
    '&features=datepicker,timezone' +
    '&countries=25,10,110,4,5,48,6' +
    '&calType=day' +
    '&timeZone=17' +
    '&lang=2' +
    '&importance=3' +
    '&events=75,76,62,59,122,71,110,179,178,44,45,47,48';

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <Box sx={{ 
        display: 'flex', 
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #111827 0%, #1e293b 100%)',
        direction: 'rtl'
      }}>
        {/* App Bar */}
        <AppBar 
          position="fixed" 
          color="inherit" 
          sx={{
            width: { md: `calc(100% - ${drawerWidth}px)` },
            mr: { md: `${drawerWidth}px` },
            backgroundColor: alpha(darkTheme.palette.background.paper, 0.8),
            backdropFilter: 'blur(8px)',
            borderBottom: `1px solid ${alpha(darkTheme.palette.divider, 0.1)}`,
          }}
        >
          <Toolbar>
            <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
              {/* Action Buttons */}
              <Box display="flex" alignItems="center" gap={1.5}>
                <Avatar
                  sx={{ 
                    mr: 1, 
                    bgcolor: darkTheme.palette.secondary.main,
                    cursor: 'pointer',
                    '&:hover': {
                      boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                    }
                  }}
                >
                  <AccountCircleIcon />
                </Avatar>
                
                <Button 
                  variant="outlined" 
                  color="secondary" 
                  startIcon={<ExitToAppIcon />}
                  sx={{
                    borderRadius: 8,
                    boxShadow: 'none',
                    fontWeight: 500,
                    textTransform: 'none',
                    height: 40,
                  }}
                  onClick={handleLogout}
                >
                  התנתק
                </Button>
                
       
                

                
                <Tooltip title="תלונות">
                  <Badge 
                    badgeContent={complaints.length} 
                    color="error"
                    overlap="circular"
                    sx={{ mr: 1 }}
                  >
                    <IconButton
                      onClick={handleToggleComplaintsDrawer}
                      sx={{ 
                        bgcolor: alpha(darkTheme.palette.error.main, 0.1),
                        color: darkTheme.palette.error.main,
                        '&:hover': {
                          bgcolor: alpha(darkTheme.palette.error.main, 0.2),
                        }
                      }}
                    >
                      <FeedbackIcon />
                    </IconButton>
                  </Badge>
                </Tooltip>
              </Box>
              
              {/* Logo and Title */}
              <Box display="flex" alignItems="center">
                <Typography variant="h6" fontWeight="bold" noWrap>
                  פלטפורמת מאיץ הרווחים - בק טסט
                </Typography>
                <Avatar 
                  sx={{ 
                    bgcolor: alpha(darkTheme.palette.primary.main, 0.2), 
                    color: darkTheme.palette.primary.main,
                    mr: 2 
                  }}
                >
                  <ShowChartIcon />
                </Avatar>
              </Box>
            </Box>
          </Toolbar>
        </AppBar>

        {/* Sidebar */}
        <Drawer
          variant="permanent"
          anchor="right"
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              borderLeft: 'none',
              backgroundColor: alpha(darkTheme.palette.background.paper, 0.9),
              boxShadow: '-1px 0 10px rgba(0,0,0,0.15)',
            },
          }}
        >
          <Toolbar />
          <Box sx={{ overflow: 'auto', p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Reports Panel Title */}
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                mb: 2, 
                pb: 2, 
                borderBottom: `1px solid ${alpha(darkTheme.palette.divider, 0.3)}`
              }}
            >
              <CalendarTodayIcon color="primary" sx={{ ml: 1 }} />
              <Typography variant="h6" color="primary" fontWeight="bold">פאנל דיווחים</Typography>
            </Box>
            
            {/* Economic Calendar */}
            <Box sx={{ flex: 1, mb: 2 }}>
              <iframe
                src={iFrameUrl}
                width="100%"
                height="500"
                frameBorder="0"
                allowTransparency="true"
                marginWidth="0"
                marginHeight="0"
                style={{ 
                  maxWidth: '100%',
                  borderRadius: 12,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                }}
              />
            </Box>

            {/* New Complaint Button */}
            <Button
              variant="contained"
              color="error"
              fullWidth
              onClick={() => setComplaintDialogOpen(true)}
              sx={{ 
                mb: 2, 
                borderRadius: 10, 
                height: 46,
                boxShadow: '0 4px 10px rgba(211, 47, 47, 0.3)'
              }}
              startIcon={<FeedbackIcon />}
            >
              שלח תלונה חדשה
            </Button>

            {/* Notes Section */}
            <Paper
              sx={{
                p: 2.5,
                borderRadius: 3,
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                border: `1px solid ${alpha(darkTheme.palette.primary.main, 0.2)}`,
                bgcolor: alpha(darkTheme.palette.background.paper, 0.8),
              }}
            >
              <Typography variant="subtitle1" align="center" fontWeight="bold" color="primary" sx={{ mb: 1 }}>
                הערות
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box
                sx={{
                  minHeight: '150px',
                  maxHeight: '300px',
                  overflowY: 'auto',
                  px: 1,
                }}
              >
                {notes.length === 0 ? (
                  <Typography variant="body2" align="center" color="text.secondary" sx={{ py: 4 }}>
                    אין הערות להצגה
                  </Typography>
                ) : (
                  notes.map((note) => (
                    <Box 
                      key={note.id} 
                      sx={{ 
                        mb: 2,
                        p: 1.5,
                        borderRadius: 2,
                        backgroundColor: alpha(darkTheme.palette.background.default, 0.6),
                      }}
                    >
                      <Typography variant="body2">{note.text}</Typography>
                    </Box>
                  ))
                )}
              </Box>
            </Paper>
          </Box>
        </Drawer>

        {/* Floating Complaints Drawer */}
        <Drawer
          anchor="left"
          open={complaintsDrawerOpen}
          onClose={() => setComplaintsDrawerOpen(false)}
          sx={{
            '& .MuiDrawer-paper': {
              width: 320,
              boxSizing: 'border-box',
              backgroundColor: darkTheme.palette.background.paper,
              boxShadow: '1px 0 10px rgba(0,0,0,0.3)',
              padding: 0,
            },
          }}
        >
          <Box sx={{ 
            padding: '16px 20px',
            backgroundColor: alpha(darkTheme.palette.error.main, 0.1),
            borderBottom: `1px solid ${alpha(darkTheme.palette.error.main, 0.3)}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <IconButton 
              size="small" 
              onClick={() => setComplaintsDrawerOpen(false)}
              sx={{
                color: darkTheme.palette.error.main,
                backgroundColor: alpha(darkTheme.palette.error.main, 0.1),
                '&:hover': {
                  backgroundColor: alpha(darkTheme.palette.error.main, 0.2),
                }
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
            <Typography variant="h6" color="error.main" fontWeight="bold">
              <FeedbackIcon sx={{ mr: 1, verticalAlign: 'bottom' }} />
              תלונות
            </Typography>
          </Box>
          
          <List sx={{ 
            padding: 0,
            maxHeight: 'calc(100vh - 180px)',
            overflow: 'auto'
          }}>
            {complaints.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  אין תלונות להצגה
                </Typography>
              </Box>
            ) : (
              complaints.map((complaint) => (
                <Box key={complaint.id} sx={{ 
                  padding: '16px 20px',
                  borderBottom: `1px solid ${alpha(darkTheme.palette.divider, 0.2)}`,
                }}>
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    marginBottom: 1
                  }}>
                    <Typography variant="subtitle2" fontWeight="medium">{complaint.userName || 'אנונימי'}</Typography>
                    <Typography variant="caption" color="text.secondary">{complaint.formattedDate || 'לא ידוע'}</Typography>
                  </Box>
                  <Paper sx={{ 
                    padding: 2,
                    backgroundColor: alpha(darkTheme.palette.background.default, 0.5),
                    borderRadius: 2
                  }}>
                    <Typography variant="body2">{complaint.complaint}</Typography>
                  </Paper>
                </Box>
              ))
            )}
          </List>

          <Box sx={{ 
            padding: 2,
            borderTop: `1px solid ${alpha(darkTheme.palette.divider, 0.2)}`,
            backgroundColor: darkTheme.palette.background.paper,
            position: 'sticky',
            bottom: 0
          }}>
            <Button
              fullWidth
              variant="contained"
              color="error"
              startIcon={<FeedbackIcon />}
              onClick={() => setComplaintDialogOpen(true)}
              sx={{ borderRadius: 8 }}
            >
              תלונה חדשה
            </Button>
          </Box>
        </Drawer>

        {/* Main Content */}
        <Box component="main" sx={{ 
          flexGrow: 1, 
          p: 3,
          mt: 8,
          width: { md: `calc(100% - ${drawerWidth}px)` },
        }}>
          {/* Action Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4, alignItems: 'center' }}>
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Button
                variant="outlined"
                startIcon={<FileUploadIcon />}
                component="label"
                sx={{
                  borderRadius: 10,
                  height: 46,
                  borderWidth: 1.5,
                }}
              >
                ייבא CSV
                <input type="file" onChange={handleImportCSV} hidden />
              </Button>
              <Button
                variant="outlined"
                startIcon={<FileDownloadIcon />}
                onClick={handleExportCSV}
                sx={{
                  borderRadius: 10,
                  height: 46,
                  borderWidth: 1.5,
                }}
              >
                ייצא CSV
              </Button>
            </Box>
            
            <Button
              variant="contained"
              color="primary"
              startIcon={showTradeForm ? <CloseIcon /> : <AddCircleOutlineIcon />}
              onClick={() => setShowTradeForm(!showTradeForm)}
              sx={{
                px: 4,
                py: 1.5,
                fontSize: '1rem',
                height: 46,
                borderRadius: 10,
                boxShadow: '0 6px 12px rgba(58, 134, 255, 0.3)',
              }}
            >
              {showTradeForm ? 'סגור טופס' : 'הוסף עסקה חדשה'}
            </Button>
          </Box>

          {/* Statistics Cards */}
          <Grid container spacing={2.5} sx={{ mb: 4 }}>
            {/* Win Rate Card */}
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ 
                borderTop: `4px solid ${darkTheme.palette.success.main}`,
                background: `linear-gradient(135deg, ${alpha(darkTheme.palette.background.paper, 0.8)} 0%, ${alpha(darkTheme.palette.success.dark, 0.1)} 100%)`,
              }}>
                <CardContent sx={{ px: 3, py: 2.5 }}>
                  <Typography 
                    variant="subtitle2" 
                    align="center" 
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    <TrendingUpIcon fontSize="small" sx={{ ml: 0.5, verticalAlign: 'middle' }} />
                    אחוז הצלחה
                  </Typography>
                  <Typography 
                    variant="h4" 
                    align="center" 
                    fontWeight="bold"
                    color={winRate > 50 ? 'success.main' : 'error.main'}
                  >
                    {winRate.toFixed(1)}%
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            {/* Total R Card */}
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ 
                borderTop: `4px solid ${darkTheme.palette.info.main}`,
                background: `linear-gradient(135deg, ${alpha(darkTheme.palette.background.paper, 0.8)} 0%, ${alpha(darkTheme.palette.info.dark, 0.1)} 100%)`,
              }}>
                <CardContent sx={{ px: 3, py: 2.5 }}>
                  <Typography 
                    variant="subtitle2" 
                    align="center" 
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    <ShowChartIcon fontSize="small" sx={{ ml: 0.5, verticalAlign: 'middle' }} />
                    סה"כ R
                  </Typography>
                  <Typography 
                    variant="h4" 
                    align="center" 
                    fontWeight="bold"
                    color={totalR > 0 ? 'success.main' : 'error.main'}
                  >
                    {totalR.toFixed(2)}
                  </Typography>
                  {/* Average value */}
                  <Typography 
                    variant="caption" 
                    align="center" 
                    color="text.secondary"
                    sx={{ display: 'block', mt: -0.1 }}
                  >
                    ממוצע: {avgR.toFixed(2)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            {/* Active Trigger Card */}
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ 
                borderTop: `4px solid ${darkTheme.palette.warning.main}`,
                background: `linear-gradient(135deg, ${alpha(darkTheme.palette.background.paper, 0.8)} 0%, ${alpha(darkTheme.palette.warning.dark, 0.1)} 100%)`,
              }}>
                <CardContent sx={{ px: 3, py: 2.5 }}>
                  <Typography 
                    variant="subtitle2" 
                    align="center" 
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    <LabelIcon fontSize="small" sx={{ ml: 0.5, verticalAlign: 'middle' }} />
                    טריגר פעיל
                  </Typography>
                  <Typography 
                    variant="h6" 
                    align="center" 
                    fontWeight="bold"
                  >
                    {currentTrigger}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            {/* Trading Hours Card */}
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ 
                borderTop: `4px solid ${darkTheme.palette.secondary.main}`,
                background: `linear-gradient(135deg, ${alpha(darkTheme.palette.background.paper, 0.8)} 0%, ${alpha(darkTheme.palette.secondary.dark, 0.1)} 100%)`,
              }}>
                <CardContent sx={{ px: 3, py: 2.5 }}>
                  <Typography 
                    variant="subtitle2" 
                    align="center" 
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    <AccessTimeIcon fontSize="small" sx={{ ml: 0.5, verticalAlign: 'middle' }} />
                    שעות מסחר
                  </Typography>
                  <Typography 
                    variant="h6" 
                    align="center" 
                    fontWeight="bold"
                  >
                    {tradingHours}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            {/* Executed Trades Card */}
            <Grid item xs={12} sm={4} md={4}>
              <Card sx={{ 
                background: `linear-gradient(135deg, ${alpha(darkTheme.palette.background.paper, 0.8)} 0%, ${alpha(darkTheme.palette.primary.dark, 0.1)} 100%)`,
              }}>
                <CardContent sx={{ px: 3, py: 2.5 }}>
                  <Typography 
                    variant="subtitle2" 
                    align="center" 
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    עסקאות שבוצעו
                  </Typography>
                  <Typography 
                    variant="h4" 
                    align="center" 
                    fontWeight="bold"
                    color="primary.main"
                  >
                    {executedTradesCount}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            {/* Remaining Trades Card */}
            <Grid item xs={12} sm={4} md={4}>
              <Card sx={{ 
                background: `linear-gradient(135deg, ${alpha(darkTheme.palette.background.paper, 0.8)} 0%, ${alpha(darkTheme.palette.success.dark, 0.1)} 100%)`,
              }}>
                <CardContent sx={{ px: 3, py: 2.5 }}>
                  <Typography 
                    variant="subtitle2" 
                    align="center" 
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    עסקאות נותרו
                  </Typography>
                  <Typography 
                    variant="h4" 
                    align="center" 
                    fontWeight="bold"
                    color="success.main"
                  >
                    {tradesLeft}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            {/* Today's Remaining Trades Card */}
            <Grid item xs={12} sm={4} md={4}>
              <Card sx={{ 
                background: `linear-gradient(135deg, ${alpha(darkTheme.palette.background.paper, 0.8)} 0%, ${alpha(darkTheme.palette.warning.dark, 0.1)} 100%)`,
                position: 'relative',
                overflow: 'visible'
              }}>
                {tradesTodayCount >= 50 && (
                  <Box sx={{
                    position: 'absolute',
                    top: -10,
                    right: -10,
                    backgroundColor: darkTheme.palette.success.main,
                    borderRadius: '50%',
                    width: 40,
                    height: 40,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                    animation: 'pulse 2s infinite',
                    '@keyframes pulse': {
                      '0%': {
                        transform: 'scale(1)',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                      },
                      '50%': {
                        transform: 'scale(1.1)',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                      },
                      '100%': {
                        transform: 'scale(1)',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                      }
                    }
                  }}>
                    <EmojiEventsIcon sx={{ color: 'white', fontSize: 24 }} />
                  </Box>
                )}
                <CardContent sx={{ px: 3, py: 2.5 }}>
                  <Typography 
                    variant="subtitle2" 
                    align="center" 
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    עסקאות נותרו היום
                  </Typography>
                  <Typography 
                    variant="h4" 
                    align="center" 
                    fontWeight="bold"
                    color={tradesLeftToday === 0 ? 'success.main' : 'warning.main'}
                  >
                    {tradesLeftToday}
                  </Typography>
                  {tradesTodayCount >= 50 && (
                    <Typography 
                      variant="caption" 
                      align="center" 
                      color="success.main"
                      sx={{ display: 'block', mt: 0.5, fontWeight: 'bold' }}
                    >
                      יעד יומי הושג! 🎉
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
            
            {/* Total Days Recorded Card */}
            <Grid item xs={12} sm={6} md={6}>
              <Card sx={{ 
                background: `linear-gradient(135deg, ${alpha(darkTheme.palette.background.paper, 0.8)} 0%, ${alpha(darkTheme.palette.info.dark, 0.1)} 100%)`,
                borderTop: `4px solid ${darkTheme.palette.info.main}`,
              }}>
                <CardContent sx={{ px: 3, py: 2.5 }}>
                  <Typography 
                    variant="subtitle2" 
                    align="center" 
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    <CalendarTodayIcon fontSize="small" sx={{ ml: 0.5, verticalAlign: 'middle' }} />
                    ימים תועדו סך הכל
                  </Typography>
                  <Typography 
                    variant="h4" 
                    align="center" 
                    fontWeight="bold"
                    color="info.main"
                  >
                    {uniqueDaysRecorded}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            {/* Current Week Days Recorded Card */}
            <Grid item xs={12} sm={6} md={6}>
              <Card sx={{ 
                background: `linear-gradient(135deg, ${alpha(darkTheme.palette.background.paper, 0.8)} 0%, ${alpha(darkTheme.palette.secondary.dark, 0.1)} 100%)`,
                borderTop: `4px solid ${darkTheme.palette.secondary.main}`,
              }}>
                <CardContent sx={{ px: 3, py: 2.5 }}>
                  <Typography 
                    variant="subtitle2" 
                    align="center" 
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    <CalendarTodayIcon fontSize="small" sx={{ ml: 0.5, verticalAlign: 'middle' }} />
                    ימים תועדו השבוע
                  </Typography>
                  <Typography 
                    variant="h4" 
                    align="center" 
                    fontWeight="bold"
                    color="secondary.main"
                  >
                    {currentWeekDaysRecorded}
                  </Typography>
                  <Typography 
                    variant="caption" 
                    align="center" 
                    color="text.secondary"
                    sx={{ display: 'block', mt: 0.5 }}
                  >
                    מתוך 7 ימים
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Trade Form Component */}
          {showTradeForm && (
            <TradeForm 
              user={user}
              userProfile={userProfile}
              setShowTradeForm={setShowTradeForm}
              showErrorMessage={showErrorMessage}
              setSnackbarMessage={setSnackbarMessage}
              setSnackbarOpen={setSnackbarOpen}
              setIsSaving={setIsSaving}
            />
          )}

          {/* Trade Table Component */}
          <TradeTable 
            trades={trades}
            user={user}
            userProfile={userProfile}
            showErrorMessage={showErrorMessage}
          />

          {/* Dialogs and Snackbars */}
          
          {/* Complaint Dialog */}
          <Dialog
            open={complaintDialogOpen}
            onClose={() => setComplaintDialogOpen(false)}
            maxWidth="xs"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: 2,
                boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                overflow: 'hidden',
                backgroundColor: alpha(darkTheme.palette.background.paper, 0.95),
                direction: 'rtl',
                textAlign: 'right'
              },
            }}
          >
            <DialogTitle sx={{ 
              pb: 1, 
              textAlign: 'center',
              borderBottom: `1px solid ${alpha(darkTheme.palette.divider, 0.3)}`,
              bgcolor: alpha(darkTheme.palette.error.main, 0.05)
            }}>
              <Typography variant="h6" fontWeight="bold" color="error" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FeedbackIcon sx={{ ml: 1 }} />
                תיבת תלונות
              </Typography>
            </DialogTitle>
            <DialogContent sx={{ pt: 3 }}>
              <TextField
                label="כתוב את התלונה שלך כאן"
                value={complaintText}
                onChange={(e) => setComplaintText(e.target.value)}
                fullWidth
                multiline
                rows={4}
                variant="outlined"
                size="small"
                sx={{
                  '& .MuiInputBase-root': {
                    height: 'auto',
                    alignItems: 'flex-start',
                    borderRadius: 2,
                    backgroundColor: alpha(darkTheme.palette.background.default, 0.7),
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: alpha(darkTheme.palette.error.main, 0.2)
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: alpha(darkTheme.palette.error.main, 0.3),
                  },
                  '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: darkTheme.palette.error.main,
                  }
                }}
              />
            </DialogContent>
            <Box sx={{ px: 3, pb: 3, pt: 1, display: 'flex', justifyContent: 'space-between' }}>
              <Button 
                onClick={() => setComplaintDialogOpen(false)}
                variant="outlined"
                sx={{
                  borderRadius: 8,
                  height: 44
                }}
              >
                ביטול
              </Button>
              <Button 
                variant="contained" 
                color="error"
                onClick={handleSaveComplaint}
                disabled={!complaintText.trim()}
                startIcon={<SendIcon />}
                sx={{
                  borderRadius: 8,
                  height: 44
                }}
              >
                שלח תלונה
              </Button>
            </Box>
          </Dialog>

          {/* Daily Goal Celebration Dialog */}
          <Dialog
            open={showDailyGoalCelebration}
            onClose={() => setShowDailyGoalCelebration(false)}
            maxWidth="sm"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: 3,
                boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                overflow: 'hidden',
                backgroundColor: alpha(darkTheme.palette.background.paper, 0.98),
                direction: 'rtl',
                textAlign: 'center',
                background: `linear-gradient(135deg, ${alpha(darkTheme.palette.success.dark, 0.1)} 0%, ${alpha(darkTheme.palette.background.paper, 0.98)} 50%, ${alpha(darkTheme.palette.success.dark, 0.1)} 100%)`,
              },
            }}
          >
            <DialogContent sx={{ pt: 5, pb: 5 }}>
              <Box sx={{ 
                animation: 'bounce 1s ease-in-out',
                '@keyframes bounce': {
                  '0%, 100%': { transform: 'translateY(0)' },
                  '50%': { transform: 'translateY(-20px)' }
                }
              }}>
                <CelebrationIcon sx={{ 
                  fontSize: 80, 
                  color: darkTheme.palette.success.main,
                  mb: 3,
                  filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))'
                }} />
              </Box>
              
              <Typography variant="h4" fontWeight="bold" color="success.main" gutterBottom>
                כל הכבוד! 🎉
              </Typography>
              
              <Typography variant="h6" color="text.primary" sx={{ mb: 2 }}>
                סיימת את היעד היומי של תיעוד עסקאות!
              </Typography>
              
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                תיעדת 50 עסקאות היום. המשך כך!
              </Typography>
              
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                gap: 2,
                flexWrap: 'wrap',
                mb: 3
              }}>
                <Chip 
                  icon={<EmojiEventsIcon />}
                  label="יעד יומי הושג"
                  color="success"
                  sx={{ fontSize: '1rem', py: 2.5, px: 1 }}
                />
                <Chip 
                  icon={<TrendingUpIcon />}
                  label={`${tradesTodayCount} עסקאות היום`}
                  color="primary"
                  sx={{ fontSize: '1rem', py: 2.5, px: 1 }}
                />
              </Box>
              
              <Button
                variant="contained"
                color="success"
                onClick={() => setShowDailyGoalCelebration(false)}
                sx={{
                  borderRadius: 10,
                  px: 5,
                  py: 1.5,
                  fontSize: '1.1rem',
                  boxShadow: '0 6px 20px rgba(16, 185, 129, 0.4)',
                }}
              >
                המשך לתעד
              </Button>
            </DialogContent>
          </Dialog>

          {/* Loading Dialog */}
          <Dialog 
            open={isSaving} 
            PaperProps={{
              sx: {
                borderRadius: 2,
                boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                padding: 3,
                textAlign: 'center',
                backgroundColor: alpha(darkTheme.palette.background.paper, 0.95),
              },
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3 }}>
              <CircularProgress size={60} sx={{ mb: 2 }} />
              <Typography variant="h6" fontWeight="bold">
                שומר...
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                אנא המתן בזמן שמירת נתוני העסקה שלך
              </Typography>
            </Box>
          </Dialog>

          {/* Error Snackbar */}
          <Snackbar
            open={errorSnackbarOpen}
            autoHideDuration={6000}
            onClose={handleCloseErrorSnackbar}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert 
              onClose={handleCloseErrorSnackbar} 
              severity="error" 
              variant="filled"
              sx={{ 
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                fontWeight: 500,
                fontSize: '0.95rem',
                py: 1,
                borderRadius: 2
              }}
            >
              {errorSnackbarMessage}
            </Alert>
          </Snackbar>

          {/* Success Snackbar */}
          <Snackbar
            open={snackbarOpen}
            autoHideDuration={6000}
            onClose={() => setSnackbarOpen(false)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          >
            <Alert 
              onClose={() => setSnackbarOpen(false)} 
              severity="success" 
              variant="filled"
              sx={{ 
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                fontWeight: 500,
                fontSize: '0.95rem',
                py: 1,
                borderRadius: 2
              }}
            >
              {snackbarMessage}
            </Alert>
          </Snackbar>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default Dashboard;